// src/views/admin/prediction/index.jsx
"use client"

import { useMemo, useState, useRef, useEffect, useCallback } from "react"
import {
  Box, Button, Flex, Grid, GridItem, Heading, Text,
  FormControl, FormLabel, Input, Select, NumberInput, NumberInputField,
  useToast, useColorModeValue, Badge, SimpleGrid, VStack, HStack, Container,
  Progress, Alert, AlertIcon, IconButton, useDisclosure,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
  Table, Thead, Tbody, Tr, Th, Td, Tag, Spinner, Divider, Collapse, Stack, FormHelperText,
  Portal,
} from "@chakra-ui/react"
import { CheckIcon, ChevronRightIcon, ChevronLeftIcon, ChevronDownIcon } from "@chakra-ui/icons"
import ArrowStepper from "components/stepper/ArrowStepper"
import { useBreakpointValue } from "@chakra-ui/react"
import { useNavigate } from "react-router-dom"
import { runPredict, listPredictions, getPredictMeta } from "services/predict"
import { listRuns } from "services/training"
import { listDistricts, listRegions } from "services/regions"
import { Wand2 } from "lucide-react"
import { useAuth } from "contexts/AuthContext"


// 옵션들
const 공법옵션 = ["순타", "역타"];
const 유형옵션 = ["아파트", "오피스텔", "주상복합"]
const DEFAULT_FINISH_LEVEL_OPTIONS = ["WHITE","WHITE+견적조건","WHITE+도면기준","Xi ella 기준","Xi ella+도면기준","Xi rene 기준","Xi rene+도면기준","Xi rene+디자인보고"]
const DEFAULT_HOUSEHOLD_WINDOW_OPTIONS = ["이중창","입면분할이중창","유리난간이중창"]
const DEFAULT_STRUCTURE_OPTIONS = {
  parking: ["O","X"],
  ground: ["O","X"],
  standard: ["O","X"],
}
const DEFAULT_STRUCTURE_VISIBILITY = {
  parking: true,
  ground: true,
  standard: true,
}
const STRUCTURE_FIELD_META = {
  structure_parking: { label: "주차장", optionKey: "parking" },
  structure_ground: { label: "지상층", optionKey: "ground" },
  structure_standard: { label: "기준층", optionKey: "standard" },
}
const STRUCTURE_FIELD_KEYS = Object.keys(STRUCTURE_FIELD_META)
const 난방옵션 = ["개별", "중앙", "지역"]
const SLOPE_OPTION_LABELS = {
  FLAT: "택지 부지 (FLAT)",
  SLOPE: "경사지 부지 (SLOPE)",
}
const 포함제외옵션 = ["제외", "포함"]

export default function Prediction() {
  const isMobile = useBreakpointValue({ base: true, md: false })
  const toast = useToast()
  const formSectionRef = useRef(null)
  const navigate = useNavigate()
  const { user = {} } = useAuth?.() || {}
  const canEditCostInputs = ["admin", "super_admin"].includes(user?.role)

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0 })
    }
  }, [])

  // 히어로(비디오) 스케일 (폼 진입 시 동결)
  const heroRef = useRef(null)
  const [heroScale, setHeroScale] = useState(1)
  const heroScaleRef = useRef(1)
  const rafRef = useRef(0)
  const [heroActive, setHeroActive] = useState(true)

  useEffect(() => {
    if (!heroActive) return
    const update = () => {
      const el = heroRef.current
      if (!el) return
      const h = el.offsetHeight || window.innerHeight
      const y = window.scrollY
      const p = Math.min(Math.max(y / h, 0), 1)
      const maxScale = isMobile ? 2.3 : 2.7
      const prefersReduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches
      const next = prefersReduced ? 1 : 1 + (maxScale - 1) * p
      if (Math.abs(next - heroScaleRef.current) > 0.0001) {
        heroScaleRef.current = next
        setHeroScale(next)
      }
    }
    const onScroll = () => {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = requestAnimationFrame(update)
    }
    const onResize = onScroll
    update()
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onResize)
    return () => {
      cancelAnimationFrame(rafRef.current)
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onResize)
    }
  }, [isMobile, heroActive])

  // 공통 스타일
  const bgGradient = useColorModeValue("linear(to-br, gray.50, blue.50)", "linear(to-br, gray.900, blue.900)")
  const cardBg = useColorModeValue("white", "gray.800")
  const cardShadow = useColorModeValue("xl", "dark-lg")
  const border = useColorModeValue("gray.100", "gray.700")
  const accent = useColorModeValue("blue.500", "blue.300")
  const subtle = useColorModeValue("gray.600", "gray.400")
  const stepperBg = useColorModeValue("white", "gray.800")
  const inputBg = useColorModeValue("white", "gray.700")
  const textColor = useColorModeValue("gray.800", "white")
  const infoBg = useColorModeValue("gray.50", "gray.700")
  const fieldFocusStyles = {
    borderColor: accent,
    boxShadow: `0 0 0 1px ${accent}`,
  }
  const fieldProps = {
    bg: inputBg,
    border: "1px solid",
    borderColor: border,
    _focus: fieldFocusStyles,
  }
  const readOnlyFieldProps = {
    ...fieldProps,
    _readOnly: { bg: "white" },
  }

  const SectionCard = ({ icon, title, helper, children }) => (
    <Box border="1px solid" borderColor={border} bg="white" rounded="xl" p={{ base: 3, md: 4 }} shadow="sm" w="100%">
      <HStack spacing={2} mb={2} align="flex-start">
        <Text as="span" fontSize="lg" aria-hidden>{icon}</Text>
        <VStack spacing={0} align="start">
          <Heading size="sm" color={textColor}>{title}</Heading>
          {helper && <Text fontSize="xs" color="gray.500">{helper}</Text>}
        </VStack>
      </HStack>
      {children}
    </Box>
  )
  // 숫자 파서 (선택값)
  const toNumOrNull = (v) => {
    const n = Number(v)
    return Number.isFinite(n) && n > 0 ? n : null
  }
  const toNonnegOrNull = (v) => {
    const n = Number(v)
    return Number.isFinite(n) && n >= 0 ? n : null
  }
  // 스텝/제출
  const [step, setStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const loadingStartRef = useRef(0)
  const MIN_LOADING_DURATION_MS = 2500
  const [progressPercent, setProgressPercent] = useState(0)
  const progressIntervalRef = useRef(null)

  // STEP 1
  const [s1, setS1] = useState({ 유형: "", 공사명: "", 지역: "", 구: "", 접수일: "" })
  // IME-safe drafts
  const [nameDraft, setNameDraft] = useState("")
  const [isNameComposing, setIsNameComposing] = useState(false)
  const nameInputRef = useRef(null)
  useEffect(() => { setNameDraft(s1.공사명 || "") }, [s1.공사명])

  const [regionOptions, setRegionOptions] = useState([])
  const [districtOptions, setDistrictOptions] = useState([])
  const [regionsLoading, setRegionsLoading] = useState(false)
  const [districtsLoading, setDistrictsLoading] = useState(false)

  useEffect(() => {
    let active = true
    const loadRegions = async () => {
      setRegionsLoading(true)
      try {
        const list = await listRegions()
        if (!active) return
        setRegionOptions(Array.isArray(list) ? list : [])
      } catch (error) {
        if (!active) return
        toast({
          status: "error",
          title: "시/도 조회 실패",
          description: String(error?.message || error),
        })
      } finally {
        if (active) {
          setRegionsLoading(false)
        }
      }
    }

    loadRegions()
    return () => {
      active = false
    }
  }, [toast])

  useEffect(() => {
    if (!s1.지역) {
      setDistrictOptions([])
      setDistrictsLoading(false)
      if (s1.구) setS1((prev) => ({ ...prev, 구: "" }))
      return
    }

    const selectedRegion = regionOptions.find((option) => option.sidoNm === s1.지역)
    if (!selectedRegion) {
      setDistrictOptions([])
      setDistrictsLoading(false)
      if (s1.구) setS1((prev) => ({ ...prev, 구: "" }))
      return
    }

    let active = true
    setDistrictsLoading(true)
    setDistrictOptions([])

    const loadDistricts = async () => {
      try {
        const list = await listDistricts({ sidoCd: selectedRegion.sidoCd })
        if (!active) return
        const normalized = Array.isArray(list) ? list : []
        setDistrictOptions(normalized)
        if (!normalized.some((item) => item.sggNm === s1.구)) {
          setS1((prev) => ({ ...prev, 구: "" }))
        }
      } catch (error) {
        if (!active) return
        toast({
          status: "error",
          title: "구 조회 실패",
          description: String(error?.message || error),
        })
      } finally {
        if (active) {
          setDistrictsLoading(false)
        }
      }
    }

    loadDistricts()
    return () => {
      active = false
    }
  }, [regionOptions, s1.지역, s1.구, toast])

  // STEP 2 
  const [s2, setS2] = useState({
    연면적: "", 마감수준: "", "세대 창호": "", 평균평형: "", 공기: "",
    지상층수: "", 지하층수: "", 난방: "개별", 세대수: "",
    단지외공사: "",
    structure_parking: "", structure_ground: "", structure_standard: "",
    공사비: "", 건축비: "", 암반비율: "", 토목: "", 전기: "",
    설비: "", 경상비: "", 기타: "", 공통: "", 공법: "",
    // 신규 입력
    공급면적_m2: "",
    "경사 여부": "",
    "동수": "",
    "대지면적(㎡)": "",
    "해안가 여부": "0",
  })
  const componentCostKeys = ["건축비", "토목", "전기", "설비", "경상비", "기타", "공통"];
  const componentCostSum = componentCostKeys.reduce((sum, key) => {
    const value = Number(s2[key]);
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0);

  // 유효성
  const step1OK = useMemo(() => {
    const 공사명값 = isNameComposing ? nameDraft : (nameDraft || s1.공사명)
    return Boolean(s1.유형 && 공사명값 && s1.지역 && s1.구 && s1.접수일)
  }, [s1.유형, s1.공사명, s1.지역, s1.구, s1.접수일, isNameComposing, nameDraft])

  const step2OK = useMemo(() => {
    const gt0 = (v) => {
      const f = parseFloat(v)
      return Number.isFinite(f) && f > 0
    }
    const slopeOk = Boolean(s2["경사 여부"]) // FLAT/SLOPE 중 하나
    const bCountOk = gt0(s2["동수"]) // 정수/양수
    const siteAreaOk = gt0(s2["대지면적(㎡)"]) // 양수
    const coastalVal = String(s2["해안가 여부"]) // "1" | "0"
    const coastalOk = coastalVal === "1" || coastalVal === "0"

    return (
      gt0(s2.연면적) &&
      // s2.마감수준 &&
      gt0(s2.평균평형) &&
      // gt0(s2.공기) &&
      s2.난방 &&
      s2.세대수 &&
      s2.지상층수 &&
      s2.지하층수 &&
      s2.공법 &&
      slopeOk && bCountOk && siteAreaOk && coastalOk
    )
  }, [s2])

  const [finishLevelOptions, setFinishLevelOptions] = useState(DEFAULT_FINISH_LEVEL_OPTIONS)
  const householdWindowOptions = DEFAULT_HOUSEHOLD_WINDOW_OPTIONS
  const [structureOptions, setStructureOptions] = useState(DEFAULT_STRUCTURE_OPTIONS)
  const [structureVisibilityRules, setStructureVisibilityRules] = useState({
    default: DEFAULT_STRUCTURE_VISIBILITY,
  })
  const [structureFieldOrder, setStructureFieldOrder] = useState(STRUCTURE_FIELD_KEYS)

  useEffect(() => {
    let cancelled = false
    getPredictMeta()
      .then((meta) => {
        if (cancelled) return
        if (Array.isArray(meta.finishLevels) && meta.finishLevels.length) {
          setFinishLevelOptions(meta.finishLevels)
        }
        // 세대 창호는 고정 3종(이중창/입면분할이중창/유리난간이중창)만 노출 — DB 값 머지 안 함
        if (meta.structureOptions) {
          setStructureOptions((prev) => ({
            parking: meta.structureOptions.parking || prev.parking,
            ground: meta.structureOptions.ground || prev.ground,
            standard: meta.structureOptions.standard || prev.standard,
          }))
        }
        if (meta.structureVisibility) {
          setStructureVisibilityRules((prev) => ({
            ...prev,
            ...meta.structureVisibility,
          }))
        }
        if (Array.isArray(meta.structureFields) && meta.structureFields.length) {
          const normalized = meta.structureFields.filter((key) => STRUCTURE_FIELD_KEYS.includes(key))
          if (normalized.length) {
            setStructureFieldOrder(normalized)
          }
        }
      })
      .catch((err) => {
        toast({ status: "error", title: "옵션 로딩 실패", description: String(err?.message || err), isClosable: true })
      })
    return () => {
      cancelled = true
    }
  }, [toast])

  const currentStructureVisibility = useMemo(() => {
    const typeKey = (s1.유형 || "").trim()
    const visibility =
      structureVisibilityRules[typeKey] ||
      structureVisibilityRules.default ||
      DEFAULT_STRUCTURE_VISIBILITY
    return visibility
  }, [s1.유형, structureVisibilityRules])

  const orderedStructureFields = useMemo(() => {
    return structureFieldOrder
      .map((key) => {
        const meta = STRUCTURE_FIELD_META[key]
        return meta
          ? { key, label: meta.label, optionKey: meta.optionKey }
          : null
      })
      .filter(Boolean)
  }, [structureFieldOrder])

  useEffect(() => {
    setS2((prev) => {
      let updated = false
      const next = { ...prev }
      orderedStructureFields.forEach(({ key, optionKey }) => {
        if (!currentStructureVisibility[optionKey] && next[key]) {
          next[key] = ""
          updated = true
        }
      })
      return updated ? next : prev
    })
  }, [currentStructureVisibility, orderedStructureFields])

  // Info / FormField
  const InfoItem = ({ label, value }) => (
    <Box p={4} bg={infoBg} borderRadius="lg" border="1px solid" borderColor={border}>
      <Text fontSize="xs" color={subtle} mb={1} fontWeight="500" textTransform="uppercase" letterSpacing="wide">{label}</Text>
      <Text fontWeight="600" color={textColor} fontSize="sm">{value || "-"}</Text>
    </Box>
  )

  const formatSlopeLabel = (value) => {
    return value ? (SLOPE_OPTION_LABELS[value] || value) : "-"
  }

  const [showOptionCost, setShowOptionCost] = useState(false); // 선택 사항(옵션·비용) 토글

  // 모바일 도움말
  const getMobileStepInfo = (currentStep) => ({
    1: { title: "📝 기본 정보 입력", message: "필수항목 입력 후 다음 단계로." },
    2: { title: "🏗️ 개요 입력", message: "상세 정보가 정확할수록 예측 정확도↑." },
    3: { title: "✅ 입력 정보 확인", message: "확인 후 예측 실행." },
  }[currentStep] || { title: "", message: "" })

  const scrollToForm = () => formSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })

  // 애니메이션 정착 (re-render로 인한 focus drop 방지)
  const [isVisible, setIsVisible] = useState(false)

  const animationsSettledRef = useRef(false)
  const settleAnimations = useCallback(() => {
    if (animationsSettledRef.current) return
    animationsSettledRef.current = true
    setIsVisible(true)
    setHeroActive(false)
  }, [])
  useEffect(() => {
    const ob = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) {
        settleAnimations()
        formSectionRef.current && ob.unobserve(formSectionRef.current)
      }
    }, { threshold: 0.1 })
    formSectionRef.current && ob.observe(formSectionRef.current)
    return () => ob.disconnect()
  }, [settleAnimations])

  // 첫 포인터 이벤트 뒤에 정착 (select/date native open 방해 금지)
  const onFirstPointerDown = useCallback(() => {
    if (animationsSettledRef.current) return
    setTimeout(() => settleAnimations(), 0)
  }, [settleAnimations])

  // 인풋 포커스 시에도 정착 (1회)
  const onAnyInputFocus = useCallback(() => {
    settleAnimations()
  }, [settleAnimations])

  // 이력 모달
  const historyModal = useDisclosure()
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyItems, setHistoryItems] = useState([])
  const [historyTotal, setHistoryTotal] = useState(0)
  const [historyPage, setHistoryPage] = useState(1)
  const historyPageSize = 10
  const [historyQ, setHistoryQ] = useState("")

  const openHistory = async () => {
    try {
      historyModal.onOpen()
      setHistoryLoading(true)
      const res = await listPredictions({ page: historyPage, page_size: historyPageSize, q: historyQ || undefined })
      setHistoryItems(res?.items || [])
      setHistoryTotal(res?.total || 0)
    } catch (e) {
      toast({ status: "error", title: "이전 예측 조회 실패", description: String(e?.message || e) })
    } finally {
      setHistoryLoading(false)
    }
  }

  // 비용 표시용 공통 함수 (쉼표 포맷/파싱)
  const formatCost = (val) => {
    if (val === null || val === undefined || val === "") return "";
    const num = typeof val === "number" ? val : Number(String(val).replace(/,/g, ""));
    return Number.isNaN(num) ? "" : new Intl.NumberFormat().format(num);
  };

  const parseCost = (val) => {
    if (val === null || val === undefined || val === "") return "";
    return String(val).replace(/,/g, "");
  };

  const fillFromHistory = (row) => {
    // s1
    const nextS1 = {
      유형: row?.type || "",
      공사명: row?.project_name || "",
      지역: row?.region || "",
      // 시도: row?.province || "",
      구: "",
      접수일: (row?.received_date || "").slice(0,10),
    }
    const district = row?.district || ""
    nextS1.구 = district
    setS1(nextS1)
    setNameDraft(nextS1.공사명 || "")

    // s2 (문자열 유지)
    setS2({
      연면적: row?.area_pyeong?.toString?.() ?? "",
      마감수준: row?.finish_level ?? "",
      "세대 창호": row?.household_window ?? "",
      structure_parking: row?.structure_parking ?? "",
      structure_ground: row?.structure_ground ?? "",
      structure_standard: row?.structure_standard ?? "",
      평균평형: row?.avg_pyeong?.toString?.() ?? "",
      공기: row?.duration_month?.toString?.() ?? "",
      지상층수: row?.floor_above?.toString?.() ?? "",
      지하층수: row?.floor_below?.toString?.() ?? "",
      난방: row?.heating ?? "개별",
      세대수: row?.households?.toString?.() ?? "",
      단지외공사: row?.out_of_complex ?? "",
      공사비: (row?.total_cost_u ?? row?.total_cost)?.toString?.() ?? "",
      건축비: (row?.building_cost_u ?? row?.building_cost)?.toString?.() ?? "",
      암반비율: (row?.rock_ratio ?? "")?.toString?.() ?? "",
      토목: (row?.civil_cost_u ?? row?.civil_cost)?.toString?.() ?? "",
      전기: (row?.electrical_cost_u ?? row?.electrical_cost)?.toString?.() ?? "",
      설비: (row?.facility_cost_u ?? row?.facility_cost)?.toString?.() ?? "",
      경상비: (row?.opex_cost_u ?? row?.opex_cost)?.toString?.() ?? "",
      기타: (row?.etc_cost_u ?? row?.etc_cost)?.toString?.() ?? "",
      공통: (row?.common_cost_u ?? row?.common_cost)?.toString?.() ?? "",
      공법: row?.construction_method ?? "",
      공급면적_m2: row?.supply_area?.toString?.() ?? "",
      // 신규 4개 필드 프리필
      "경사 여부": row?.slope_type ?? "",
      "동수": row?.building_count?.toString?.() ?? "",
      "대지면적(㎡)": row?.site_area?.toString?.() ?? "",
      "해안가 여부": (row?.is_coastal ? "1" : "0"),
    })

    toast({ status: "success", title: "이전 예측 불러오기 완료", duration: 1500 })
    historyModal.onClose()
    setStep(1)
    scrollToForm()
  }

  // 예측 실행
  const startProgressInterval = () => {
    stopProgressInterval()
    progressIntervalRef.current = setInterval(() => {
      setProgressPercent((prev) => {
        if (prev >= 95) return prev
        return prev + 1
      })
    }, 120)
  }

  const stopProgressInterval = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current)
      progressIntervalRef.current = null
    }
  }

  const handlePredict = async () => {
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
    let pendingRunId = null
    let showSuccessToast = true
    try {
      setProgressPercent(0)
      startProgressInterval()
      setIsSubmitting(true)
      loadingStartRef.current = Date.now()

      // 학습 실행 중 여부 확인 → 실행 중이면 안내 후 중단
      try {
        const runs = await listRuns()
        const running = Array.isArray(runs) ? runs : (runs || [])
        if ((running || []).some((r) => r?.status === 'running')) {
          toast({
            status: 'info',
            title: 'AI가 현재 학습을 진행 중입니다.',
            description: '학습이 완료된 후 예측 기능을 이용하실 수 있으니 잠시 후 다시 시도해주세요.\n실행 이력은 [학습 > 실행 기록] 메뉴에서 확인 가능합니다.',
            duration: 4000,
            isClosable: true,
            })
            setIsSubmitting(false)
            return
          }
        } catch {}
      const input = {
        유형: s1.유형,
        공사명: isNameComposing ? nameDraft : (nameDraft || s1.공사명),
        지역: s1.지역,
        구: s1.구,
        접수일: s1.접수일,
        연면적: Number(s2.연면적 || 0),
        마감수준: s2.마감수준,
        "세대 창호": s2["세대 창호"],
        평균평형: Number(s2.평균평형 || 0),
        공기: Number(s2.공기 || 0),
        지상층수: s2.지상층수 ? Number(s2.지상층수) : 0,
        지하층수: s2.지하층수 ? Number(s2.지하층수) : 0,
        난방: s2.난방,
        세대수: s2.세대수 ? Number(s2.세대수) : 0,
        단지외공사: s2.단지외공사,
        structure_parking: s2.structure_parking,
        structure_ground: s2.structure_ground,
        structure_standard: s2.structure_standard,
        공사비: toNumOrNull(s2.공사비),
        건축비: toNumOrNull(s2.건축비),
        토목비: toNumOrNull(s2.토목),
        전기비: toNumOrNull(s2.전기),
        설비비: toNumOrNull(s2.설비),
        경상비: toNumOrNull(s2.경상비),
        기타비: toNumOrNull(s2.기타),
        공통비: toNumOrNull(s2.공통), 
        공법: s2.공법,      
        // 암반비율(0 허용)
        암반비율: toNonnegOrNull(s2.암반비율),
        // 신규 입력
        "공급면적(㎡)": s2.공급면적_m2 ? Number(s2.공급면적_m2) : 0,
        "경사 여부": s2["경사 여부"],
        "동수": s2["동수"] ? Number(s2["동수"]) : 0,
        "대지면적(㎡)": s2["대지면적(㎡)"] ? Number(s2["대지면적(㎡)"]) : 0,
        "해안가 여부": s2["해안가 여부"] ? Number(s2["해안가 여부"]) : 0,
      }
      const options = {
        model_dir: "artifacts/pyeong_cci_nopv",
        cci_sheet: "건물건설및건축보수_소계",
        cci_date_col: "date",
        cci_value_col: "index",
        // cci_base: "2022-01",
        no_pv: true,
        clip_inputs: true,
        save_input_report: true,
      }
      const res = await runPredict({ input, options })
      const runId = res?.run_id
      if (!runId) throw new Error(`run_id 없음: ${JSON.stringify(res)}`)
      pendingRunId = runId
      setProgressPercent(100)
      stopProgressInterval()
    } catch (e) {
      showSuccessToast = false
      toast({ status: "error", title: "예측 실행 실패", description: String(e?.message || e), isClosable: true })
      stopProgressInterval()
      setProgressPercent(0)
    } finally {
      const elapsed = Date.now() - (loadingStartRef.current || Date.now())
      const remaining = Math.max(0, MIN_LOADING_DURATION_MS - elapsed)
      if (remaining > 0) await sleep(remaining)
      setIsSubmitting(false)
      stopProgressInterval()
      if (pendingRunId) {
        setTimeout(() => navigate(`/admin/prediction/result/${pendingRunId}`), 50)
      }
      if (showSuccessToast) {
        toast({ status: "success", title: "예측 실행 완료", description: "결과 페이지로 확인 가능합니다.", duration: 1500 })
      }
      window?.scrollTo?.({ top: 0, behavior: "smooth" })
      setProgressPercent(0)
    }
  }

  // 고정 id들 (라벨/필드 연결 안정화: 재렌더 시 포커스 유지)
  const ID = {
    name: "project-name-input",
    // step2
    area: "area-pyeong-input",
    finish: "finish-level-select",
    householdWindow: "household-window-select",
    avg: "avg-pyeong-input",
    duration: "duration-month-input",
    floorAbove: "floor-above-input",
    floorBelow: "floor-below-input",
    heating: "heating-select",
    households: "households-input",
    outOfComplex: "out-of-complex-select",
    totalCost: "total-cost-input",
    buildingCost: "building-cost-input",
    constructionMethod: "construction-method-select",
  }

  return (
    <Box minH="100vh" bg="white" position="relative">
      {/* 히어로 */}
      <Box ref={heroRef} position="relative" h="100vh" w="100%" overflow="hidden" display="flex" alignItems="center" justifyContent="center" bg={bgGradient}>
        <video src="/brand-story.mp4" autoPlay muted loop playsInline preload="metadata"
          style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover", zIndex:1, transform:`scale(${heroScale})`, willChange:"transform", transition:"transform 60ms linear" }} />
        <Flex position="absolute" top="0" left="0" w="100%" h="100%" direction="column" align="center" justify="center" bg="rgba(0,0,0,0.5)" zIndex={2} px={4}>
          <VStack spacing={6} textAlign="center" maxW="800px">
            <Text color="white" fontSize={{ base:"2xl", md:"5xl" }} fontWeight="bold" lineHeight="1.2">🏗️ 지금 바로 예상 공사비를<br/>확인해보세요!</Text>
            <Text color="whiteAlpha.900" fontSize={{ base:"md", md:"xl" }}>AI 기반 데이터 분석으로 정확하고 빠르게.</Text>
            <Button onClick={openHistory} size="sm" colorScheme="whiteAlpha" variant="outline">🕘 이전 예측 불러오기</Button>
          </VStack>
          <VStack spacing={2} position="absolute" bottom={12}>
            <Text color="white" fontSize="sm" fontWeight="500" textTransform="uppercase" letterSpacing="wide">Scroll</Text>
            <IconButton aria-label="Scroll to form" icon={<ChevronDownIcon/>} variant="ghost" color="white" size="lg" onClick={scrollToForm}/>
          </VStack>
        </Flex>
      </Box>

      {/* 폼 섹션 */}
      <Box
        ref={formSectionRef}
        onPointerDownCapture={onFirstPointerDown}
        bg={bgGradient}
        minH="100vh"
        py={16}
        transform={isVisible ? "translateY(0)" : "translateY(50px)"}
        opacity={isVisible ? 1 : 0}
        transition="all 0.8s cubic-bezier(0.4, 0, 0.2, 1)"
      >
        <Container maxW="6xl">
          <VStack spacing={8} align="stretch">
            {/* 모바일 진행/도움말 복원 */}
            {isMobile && (
              <VStack spacing={4} mx={2}>
                {/* 진행 상황 카드 */}
                <Box
                  w="100%"
                  bg={cardBg}
                  p={4}
                  borderRadius="xl"
                  shadow="md"
                  border="1px solid"
                  borderColor={border}
                >
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" fontWeight="600" color={textColor}>진행 상황</Text>
                    <Text fontSize="sm" color={subtle}>{step}/3 단계</Text>
                  </HStack>

                  {/* 모바일에서만 풀-블리드(좌우 꽉 차게) */}
                  <Box mx={{ base: -4, sm: 0 }} mt={2}>
                    <Progress
                      value={(step / 3) * 100}
                      colorScheme="blue"
                      size={{ base: "md", sm: "sm" }}     // 모바일은 조금 더 두껍게
                      borderRadius="full"
                      bg={infoBg}
                      w="100%"
                    />
                  </Box>
                </Box>

                {/* 안내 알림 */}
                <Alert
                  status="info"
                  borderRadius="xl"
                  bg={cardBg}
                  border="1px solid"
                  borderColor={border}
                  shadow="md"
                >
                  <AlertIcon color={accent} />
                  <Box>
                    <Text fontWeight="600" fontSize="sm" color={textColor} mb={1}>
                      {getMobileStepInfo(step).title}
                    </Text>
                    <Text fontSize="xs" color={subtle}>
                      {getMobileStepInfo(step).message}
                    </Text>
                  </Box>
                </Alert>
              </VStack>
            )}

            {!isMobile && (
              <Box bg={stepperBg} p={{ base:3, md:6 }} borderRadius="2xl" shadow={cardShadow} border="1px solid" borderColor={border}>
                <ArrowStepper
                  steps={["기본 정보", "공사 개요", "결과 확인"]}
                  activeIndex={step - 1}
                  onStepClick={(i) => {
                    if (i === 0) setStep(1)
                    if (i === 1 && step1OK) setStep(2)
                    if (i === 2 && step2OK) setStep(3)
                  }}
                />
              </Box>
            )}

            {/* STEP 1 */}
            {step === 1 && (
              <Box bg={cardBg} borderRadius="2xl" p={{ base:4, md:8 }} shadow={cardShadow} border="1px solid" borderColor={border}>
                <VStack align="stretch" spacing={6}>
                  <HStack justify="space-between">
                    <HStack><Box w={8} h={8} bg={accent} borderRadius="full" display="flex" alignItems="center" justifyContent="center"><Text color="white" fontWeight="bold" fontSize="sm">1</Text></Box><Heading size={{ base:"md", md:"lg" }} color={textColor}>기본 정보</Heading></HStack>
                    <Button variant="outline" onClick={openHistory}>🕘 불러오기</Button>
                  </HStack>

                  <Grid templateColumns={{ base:"1fr", md:"repeat(2,1fr)", lg:"repeat(3,1fr)" }} gap={{ base:4, md:6 }}>
                    <GridItem>
                      <FormControl isRequired>
                        <FormLabel htmlFor="type-select">유형</FormLabel>
                        <Select
                          id="type-select"
                          value={s1.유형}
                          onChange={(e) => setS1((v) => ({ ...v, 유형: e.target.value }))}
                          onFocus={onAnyInputFocus}
                          placeholder="유형을 선택하세요"
                          {...fieldProps}
                        >
                          {유형옵션.map(o => <option key={o}>{o}</option>)}
                        </Select>
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl isRequired>
                        <FormLabel htmlFor={ID.name}>공사명</FormLabel>
                        <Input
                          id={ID.name}
                          ref={nameInputRef}
                          value={nameDraft}
                          onFocus={onAnyInputFocus}
                          onChange={(e) => {
                            setNameDraft(e.target.value)
                            if (!isNameComposing) setS1((p) => ({ ...p, 공사명: e.target.value }))
                          }}
                          onCompositionStart={() => setIsNameComposing(true)}
                          onCompositionEnd={(e) => {
                            setIsNameComposing(false)
                            setNameDraft(e.target.value)
                            setS1((p) => ({ ...p, 공사명: e.target.value }))
                            requestAnimationFrame(() => nameInputRef.current?.focus())
                          }}
                          onBlur={(e) => {
                            if (!isNameComposing) setS1((p) => ({ ...p, 공사명: e.target.value }))
                          }}
                          placeholder="예: 강남 아파트 신축공사"
                          {...fieldProps}
                        />
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl isRequired>
                        <FormLabel htmlFor="region-select">시/도</FormLabel>
                        <Select
                          id="region-select"
                          value={s1.지역}
                          onChange={(e) =>
                            setS1((v) => ({ ...v, 지역: e.target.value, 구: "" }))
                          }
                          onFocus={onAnyInputFocus}
                          placeholder={regionsLoading ? "로딩 중..." : "시/도를 선택하세요"}
                          isDisabled={regionsLoading}
                          {...fieldProps}
                        >
                          {regionOptions.map((option) => (
                            <option key={option.sidoCd} value={option.sidoNm}>
                              {option.sidoNm}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                    </GridItem>

                     {/* <GridItem>
                     <FormControl isRequired>
                        <FormLabel htmlFor="province-select">시도</FormLabel>
                        <Select id="province-select" value={s1.시도} onChange={(e)=>setS1(v=>({...v, 시도:e.target.value}))}
                          onFocus={onAnyInputFocus} placeholder="시도를 선택하세요"
                          bg={inputBg} borderColor={border} _focus={{ borderColor: accent, boxShadow:`0 0 0 1px ${accent}` }}>
                          {시도옵션.map(o => <option key={o}>{o}</option>)}
                        </Select>
                      </FormControl> 
                    </GridItem>*/}

                    <GridItem>
                      <FormControl isRequired>
                        <FormLabel htmlFor="district-select">구</FormLabel>
                        <Select
                          id="district-select"
                          value={s1.구}
                          onChange={(e) => setS1((v) => ({ ...v, 구: e.target.value }))}
                          onFocus={onAnyInputFocus}
                          placeholder={
                            !s1.지역
                              ? "시를 먼저 선택하세요"
                              : districtsLoading
                              ? "로딩 중..."
                              : "구를 선택하세요"
                          }
                          isDisabled={!s1.지역 || districtsLoading || districtOptions.length === 0}
                          {...fieldProps}
                        >
                          {districtOptions.map((option) => (
                            <option key={option.sggCd} value={option.sggNm}>
                              {option.sggNm}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl isRequired>
                        <FormLabel htmlFor="received-date">접수일</FormLabel>
                        <Input
                          id="received-date"
                          type="date"
                          value={s1.접수일}
                          onChange={(e) => setS1((v) => ({ ...v, 접수일: e.target.value }))}
                          onFocus={onAnyInputFocus}
                          {...fieldProps}
                        />
                      </FormControl>
                    </GridItem>
                  </Grid>

                  <Flex justify="flex-end" pt={4}>
                    <Button colorScheme="blue" onClick={()=>setStep(2)} isDisabled={!step1OK} rightIcon={<ChevronRightIcon/>}>
                      다음
                    </Button>
                  </Flex>
                </VStack>
              </Box>
            )}

            {/* STEP 2 */}
            {step === 2 && (
            <Box
              bg={cardBg}
              borderRadius="2xl"
              p={{ base: 4, md: 8 }}
              shadow={cardShadow}
              border="1px solid"
              borderColor={border}
            >
              <VStack align="stretch" spacing={6}>
                {/* 타이틀 */}
                <HStack>
                  <Box
                    w={8}
                    h={8}
                    bg={accent}
                    borderRadius="full"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text color="white" fontWeight="bold" fontSize="sm">
                      2-1
                    </Text>
                  </Box>
                  <Heading size={{ base: "md", md: "lg" }} color={textColor}>
                    공사 개요
                  </Heading>
                </HStack>

                {/* 상단 기본정보 그리드: 모바일 1열 / md 2열 / lg 3열 */}
                <Grid
                  templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
                  gap={{ base: 4, md: 6 }}
                >
                  {/* 연면적(㎡) : 상태는 평(=㎡*0.3025)로 저장 */}
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel htmlFor={ID.area}>연면적(㎡)</FormLabel>
                      <NumberInput
                        value={s2.연면적 ? Math.round(s2.연면적 / 0.3025) : ""}
                        min={0}
                        onChange={(_, valueNum) => {
                          const py = valueNum ? valueNum * 0.3025 : 0;
                          setS2((p) => ({ ...p, 연면적: py }));
                        }}
                      >
                      <NumberInputField
                        id={ID.area}
                        placeholder="㎡ (제곱미터) 단위"
                        onFocus={onAnyInputFocus}
                        {...fieldProps}
                      />
                      </NumberInput>
                      {s2.연면적 > 0 && (
                        <Text mt={1} fontSize="sm" color="gray.600">
                          ≈ {s2.연면적.toLocaleString(undefined, { maximumFractionDigits: 2 })} 평
                        </Text>
                      )}
                    </FormControl>
                  </GridItem>

                  {/* 공급면적(㎡) */}
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel htmlFor="supply-area">공급면적(㎡)</FormLabel>
                      <NumberInput
                        value={s2.공급면적_m2 ?? ""}
                        min={0}
                        step={0.1}
                        precision={1}
                        onChange={(valueStr, valueNum) =>
                          setS2(p => {
                            // 입력을 다 지우거나, 숫자가 아닌 경우 → 상태를 비움
                            if (!Number.isFinite(valueNum)) {
                              return { ...p, 공급면적_m2: "", 평균평형: "" };
                            }
                            // 세대수가 없거나 0이면 평균평형 계산 안 함
                            const hasUnits = Number(p.세대수) > 0;
                            const avg = hasUnits ? Number(((valueNum * 0.3025) / Number(p.세대수)).toFixed(1)) : "";
                            return { ...p, 공급면적_m2: valueNum, 평균평형: avg };
                          })
                        }
                      >
                        <NumberInputField
                          id="supply-area"
                          placeholder="㎡ (제곱미터) 단위"
                          onFocus={onAnyInputFocus}
                          {...fieldProps}
                        />
                      </NumberInput>
                      {Number(s2.공급면적_m2) > 0 && (
                        <Text mt={1} fontSize="sm" color="gray.600">
                          ≈ {(Number(s2.공급면적_m2) * 0.3025).toLocaleString(undefined, { maximumFractionDigits: 2 })} 평 (총 공급면적)
                        </Text>
                      )}
                    </FormControl>
                  </GridItem>

                  {/* 평균평형(평) 읽기전용 */}
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel htmlFor={ID.avg}>평균평형(평)</FormLabel>
                      <NumberInput value={s2.평균평형 ?? 0} isReadOnly clampValueOnBlur={false}>
                      <NumberInputField
                        id={ID.avg}
                        placeholder="자동 계산"
                        onFocus={onAnyInputFocus}
                        {...readOnlyFieldProps}
                      />
                      </NumberInput>
                      <Text mt={1} fontSize="xs" color="gray.500">
                        (공급면적㎡ × 0.3025) ÷ 세대수 ={" "}
                        {s2.평균평형?.toLocaleString(undefined, { maximumFractionDigits: 1 }) ?? 0} 평
                      </Text>
                    </FormControl>
                  </GridItem>

                  {/* 대지 조건 (FLAT/SLOPE) */}
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel>
                        대지 조건
                      </FormLabel>
                      <Select
                        placeholder="선택"
                        value={s2["경사 여부"]}
                        onChange={(e) => setS2((p) => ({ ...p, "경사 여부": e.target.value }))}
                        {...fieldProps}
                      >
                        <option value="FLAT">택지 부지 (FLAT)</option>
                        <option value="SLOPE">경사지 부지 (SLOPE)</option>
                      </Select>
                    </FormControl>
                  </GridItem>

                  {/* 동수 */}
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel>
                        동수
                      </FormLabel>
                      <NumberInput value={s2["동수"] ?? ""} onChange={(v) => setS2((p)=>({ ...p, "동수": v }))} clampValueOnBlur={false}>
                        <NumberInputField {...fieldProps} />
                      </NumberInput>
                    </FormControl>
                  </GridItem>

                  {/* 대지면적(㎡) */}
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel>
                        대지면적(㎡)
                      </FormLabel>
                      <NumberInput value={s2["대지면적(㎡)"] ?? ""} onChange={(v) => setS2((p)=>({ ...p, "대지면적(㎡)": v }))} clampValueOnBlur={false}>
                        <NumberInputField {...fieldProps} />
                      </NumberInput>
                    </FormControl>
                  </GridItem>

                  {/* 수변 여부 (1/0) */}
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel>
                        수변 여부
                      </FormLabel>
                      <Select
                        placeholder="선택"
                        value={s2["해안가 여부"] ?? ""}
                        onChange={(e) => setS2((p)=>({ ...p, "해안가 여부": e.target.value }))}
                        {...fieldProps}
                      >
                        <option value="1">예</option>
                        <option value="0">아니오</option>
                      </Select>
                    </FormControl>
                  </GridItem>

                  {/* 지상층수 */}
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel htmlFor={ID.floorAbove}>지상층수</FormLabel>
                      <NumberInput value={s2.지상층수} onChange={(v) => setS2((p) => ({ ...p, 지상층수: v }))} min={0} step={1}>
                          <NumberInputField
                            id={ID.floorAbove}
                            placeholder="0"
                            onFocus={onAnyInputFocus}
                            {...fieldProps}
                          />
                      </NumberInput>
                    </FormControl>
                  </GridItem>

                  {/* 지하층수 */}
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel htmlFor={ID.floorBelow}>지하층수</FormLabel>
                      <NumberInput value={s2.지하층수} onChange={(v) => setS2((p) => ({ ...p, 지하층수: v }))} min={0} step={1}>
                          <NumberInputField
                            id={ID.floorBelow}
                            placeholder="0"
                            onFocus={onAnyInputFocus}
                            {...fieldProps}
                          />
                      </NumberInput>
                    </FormControl>
                  </GridItem>

                  {/* 난방 */}
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel htmlFor={ID.heating}>난방</FormLabel>
                      <Select
                        id={ID.heating}
                        value={s2.난방}
                        onChange={(e) => setS2((p) => ({ ...p, 난방: e.target.value }))}
                        onFocus={onAnyInputFocus}
                        {...fieldProps}
                      >
                        {난방옵션.map((o) => (
                          <option key={o}>{o}</option>
                        ))}
                      </Select>
                    </FormControl>
                  </GridItem>

                  {/* 세대수 */}
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel htmlFor={ID.households}>세대수</FormLabel>
                      <NumberInput
                        value={s2.세대수 ?? ""}         // ← 빈값 안전
                        min={0}
                        step={1}
                        onChange={(valueStr, valueNum) =>
                          setS2((p) => {
                            const units = Number.isFinite(valueNum) ? valueNum : "";
                            const supply = Number(p.공급면적_m2);
                            const avg =
                              Number.isFinite(supply) && supply > 0 &&
                              Number.isFinite(units) && units > 0
                                ? Number(((supply * 0.3025) / units).toFixed(1))
                                : "";
                            return { ...p, 세대수: units, 평균평형: avg };
                          })
                        }
                      >
                        <NumberInputField
                          id={ID.households}
                          placeholder="0"
                          onFocus={onAnyInputFocus}
                          {...fieldProps}
                        />
                      </NumberInput>
                    </FormControl>
                  </GridItem>

                  {/* 공법 */}
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel htmlFor={ID.constructionMethod}>지하공법</FormLabel>
                      <Select
                        id={ID.constructionMethod}
                        value={s2.공법}
                        onChange={(e) => setS2((p) => ({ ...p, 공법: e.target.value }))}
                        onFocus={onAnyInputFocus}
                        placeholder="공법을 선택하세요"
                        {...fieldProps}
                      >
                        {공법옵션.map((o) => (
                          <option key={o}>{o}</option>
                        ))}
                      </Select>
                    </FormControl>
                  </GridItem>

                  {/* 선택 사항 헤더 & 안내 (항상 보임) */}
                  <GridItem colSpan={{ base: 1, md: 2, lg: 3 }}>
                    <Box textAlign="left" mb={{ base: 2, md: 2 }}>
                      <Divider mb={{ base: 3, md: 4 }} />

                      {/* 헤더: 모바일은 세로(column), 데스크톱은 가로(row) 배치 */}
                      <Stack
                        direction={{ base: "column", md: "row" }}
                        justify="space-between"
                        align={{ base: "stretch", md: "center" }}
                        spacing={{ base: 2, md: 0 }}
                        w="100%"
                      >
                        {/* 좌측: 타이틀 + 안내문구(반응형) */}
                        <Box>
                          <HStack spacing={3} align="center">
                            <Box
                              w={8}
                              h={8}
                              bg={accent}
                              borderRadius="full"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              flexShrink={0}
                            >
                              <Text color="white" fontWeight="bold" fontSize="sm">2-2</Text>
                            </Box>

                            <Heading size={{ base: "md", md: "lg" }} color={textColor}>
                              선택 사항
                            </Heading>

                            {/* 데스크톱: 제목 옆에 간단 안내 */}
                            <Text
                              fontSize="sm"
                              color="gray.600"
                              ml={2}
                              display={{ base: "none", md: "inline" }}
                              mt={{ base: 2, md: 0 }}
                            >
                              일부 항목은 정확도 향상에 도움이 됩니다. <b>아래 항목을 확인하세요. 🤨</b>
                            </Text>
                          </HStack>

                          {/* 모바일: 제목 아래에 짧고 읽기 쉬운 안내 */}
                          <Text
                            fontSize="xs"
                            color="gray.600"
                            mt={1}
                            lineHeight="1.4"
                            display={{ base: "block", md: "none" }}
                          >
                            <b>아래 항목을 확인하세요. 🤨</b>
                          </Text>
                        </Box>

                        {/* 우측: 열기/닫기 토글 (모바일은 가로 꽉 채우기) */}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowOptionCost((v) => !v)}
                    rightIcon={
                      <ChevronDownIcon
                        transform={showOptionCost ? "rotate(180deg)" : "rotate(0deg)"}
                        transition="transform 0.2s"
                      />
                    }
                    alignSelf={{ base: "flex-start", md: "center" }}
                    w={{ base: "100%", md: "auto" }}
                  >
                    {showOptionCost ? "닫기" : "자세히보기"}
                  </Button>
                </Stack>
              </Box>
                  </GridItem>
                  {/* ▼ 옵션·비용: Collapse를 GridItem로 감싸고, 내부를 두 섹션(선택 입력 / 실제 비용 입력)으로 분리 */}
                  <GridItem colSpan={{ base: 1, md: 2, lg: 3 }}>
                    <Collapse in={showOptionCost} animateOpacity style={{ width: "100%" }}>
                      <VStack align="stretch" spacing={4} mt={1}>
                        <SectionCard
                          icon="🚀"
                          title="정확도 올리기"
                          helper="입력값이 많을수록 예측 정확도가 향상됩니다."
                        >
                          <VStack align="stretch" spacing={4}>
                            <Alert status="info" borderRadius="md" alignItems="flex-start">
                              <AlertIcon mt={0.5} />
                              <Text fontSize="sm">
                                <b>Q. 정확도가 낮은데 아래 정보를 더 입력하면 오를까? </b><br />
                                A. 네, 물론입니다. 아래 항목을 입력할수록 <b>비용 예측에 대한 정확도</b>가 올라갑니다.😎
                              </Text>
                            </Alert>

                            <FormControl>
                              <FormLabel>구조</FormLabel>
                              <Flex wrap="wrap" gap={3}>
                                {orderedStructureFields.map(({ key, label, optionKey }) => {
                                  if (!currentStructureVisibility[optionKey]) return null
                                  return (
                                    <Box
                                      key={key}
                                      flex={{ base: "1 1 100%", sm: "1 1 160px", md: `1 1 calc(${100 / Math.min(3, orderedStructureFields.length)}% - 16px)` }}
                                      minW="140px"
                                    >
                                      <Text fontSize="sm" mb={1} fontWeight="500">
                                        {label}
                                      </Text>
                                      <Select
                                        id={`structure-${key}`}
                                        value={s2[key] || ""}
                                        onChange={(e) => setS2((p) => ({ ...p, [key]: e.target.value }))}
                                        onFocus={onAnyInputFocus}
                                        placeholder="구조를 선택하세요"
                                        {...fieldProps}
                                      >
                                        {(structureOptions[optionKey] || []).map((optionValue) => (
                                          <option key={optionValue} value={optionValue}>
                                            {optionValue}
                                          </option>
                                        ))}
                                      </Select>
                                    </Box>
                                  )
                                })}
                              </Flex>
                              <Text fontSize="xs" color="gray.500" mt={1}>
                                유형에 따라 항목이 표시되며, 노출되지 않는 항목은 자동으로 제외됩니다.
                              </Text>
                            </FormControl>

                            <SimpleGrid
                              columns={{ base: 1, sm: 1, md: 2, lg: 3 }}
                              gap={{ base: 4, md: 5 }}
                            >
                              <GridItem>
                                <FormControl>
                                  <FormLabel htmlFor={ID.finish}>마감수준</FormLabel>
                                  <Select
                                    id={ID.finish}
                                    value={s2.마감수준}
                                    onChange={(e) => setS2((p) => ({ ...p, 마감수준: e.target.value }))}
                                    onFocus={onAnyInputFocus}
                                    placeholder="마감수준을 선택하세요"
                                    {...fieldProps}
                                  >
                                    {finishLevelOptions.map((o) => (
                                      <option key={o}>{o}</option>
                                    ))}
                                  </Select>
                                </FormControl>
                              </GridItem>

                              <GridItem>
                                <FormControl>
                                  <FormLabel htmlFor={ID.householdWindow}>세대 창호</FormLabel>
                                  <Select
                                    id={ID.householdWindow}
                                    value={s2["세대 창호"]}
                                    onChange={(e) => setS2((p) => ({ ...p, "세대 창호": e.target.value }))}
                                    onFocus={onAnyInputFocus}
                                    placeholder="세대 창호를 선택하세요"
                                    {...fieldProps}
                                  >
                                    {householdWindowOptions.map((o) => (
                                      <option key={o}>{o}</option>
                                    ))}
                                  </Select>
                                </FormControl>
                              </GridItem>

                              <GridItem>
                                <FormControl>
                                  <FormLabel htmlFor={ID.duration}>공기</FormLabel>
                                  <NumberInput
                                    value={s2.공기}
                                    onChange={(valueStr, valueNum) => setS2((p) => ({ ...p, 공기: valueNum }))}
                                    min={0}
                                    step={0.1}
                                    precision={1}
                                  >
                                    <NumberInputField
                                      id={ID.duration}
                                      placeholder="0.0"
                                      onFocus={onAnyInputFocus}
                                      {...fieldProps}
                                    />
                                  </NumberInput>
                                </FormControl>
                              </GridItem>

                              <GridItem>
                                <FormControl>
                                  <FormLabel htmlFor={ID.outOfComplex}>단지외공사</FormLabel>
                                  <Select
                                    id={ID.outOfComplex}
                                    value={s2.단지외공사}
                                    onChange={(e) => setS2((p) => ({ ...p, 단지외공사: e.target.value }))}
                                    onFocus={onAnyInputFocus}
                                    placeholder="선택하세요"
                                    {...fieldProps}
                                  >
                                    {포함제외옵션.map((o) => (
                                      <option key={o}>{o}</option>
                                    ))}
                                  </Select>
                                </FormControl>
                              </GridItem>

                              <GridItem>
                                <FormControl>
                                  <FormLabel>암반비율</FormLabel>
                                  <NumberInput
                                    value={s2.암반비율}
                                    onChange={(valueStr, valueNum) => setS2((p) => ({ ...p, 암반비율: valueNum }))}
                                    min={0}
                                    max={100}
                                  >
                                    <NumberInputField
                                      placeholder="0 ~ 100 (단위: %)"
                                      onFocus={onAnyInputFocus}
                                      {...fieldProps}
                                    />
                                  </NumberInput>
                                  <Text fontSize="xs" color="gray.500" mt={1}>
                                    풍화암 기준으로 입력하세요.
                                  </Text>
                                </FormControl>
                              </GridItem>
                            </SimpleGrid>
                          </VStack>
                        </SectionCard>

                        {canEditCostInputs ? (
                          <SectionCard
                            icon="💰"
                            title="실제 비용 입력"
                            helper="실제 실행 예산을 입력하면 예측치와 비교해 정밀도를 확인할 수 있습니다."
                          >
                            <VStack align="stretch" spacing={4}>
                              <Alert status="info" borderRadius="md" alignItems="flex-start">
                                <AlertIcon mt={1} />
                                <Box>
                                  <Text fontSize="sm" fontWeight="bold" mb={1}>
                                    Q. AI 예상 공사비 정확도는 몇 % 정확할까?
                                  </Text>
                                  <Text fontSize="sm">
                                    A. <b>공사비·건축비·토목·전기·설비·경상비·기타·공통</b> 항목은 실행 예산 확정 후 입력 시{" "}
                                    <b>AI 예측값과 비교해 정확도</b>를 확인할 수 있습니다. 😃
                                  </Text>
                                </Box>
                              </Alert>

                              <Grid
                                templateColumns={{
                                  base: "1fr",
                                  sm: "1fr",
                                  md: "repeat(2, 1fr)",
                                  lg: "repeat(3, 1fr)",
                                }}
                                gap={{ base: 4, md: 5 }}
                              >
                                {/* 총 공사비 */}
                                <GridItem>
                                  <FormControl>
                                    <FormLabel htmlFor={ID.totalCost}>총 공사비 (백만)</FormLabel>
                                    <NumberInput
                                      value={s2.공사비 ?? ""}
                                      min={0}
                                      onChange={(_, v) => setS2((p) => ({ ...p, 공사비: v }))}
                                      format={formatCost}
                                      parse={parseCost}
                                      isDisabled={!canEditCostInputs}
                                    >
                                      <NumberInputField
                                        id={ID.totalCost}
                                        placeholder="백만 단위"
                                        onFocus={onAnyInputFocus}
                                        isDisabled={!canEditCostInputs}
                                        {...fieldProps}
                                      />
                                    </NumberInput>
                                  </FormControl>
                                </GridItem>


                                {/* 건축비 */}
                                <GridItem>
                                  <FormControl>
                                    <FormLabel htmlFor={ID.buildingCost}>건축비 (원/평)</FormLabel>
                                    <NumberInput
                                      value={s2.건축비 ?? ""}
                                      min={0}
                                      onChange={(_, v) => setS2((p) => ({ ...p, 건축비: v }))}
                                      format={formatCost}
                                      parse={parseCost}
                                      isDisabled={!canEditCostInputs}
                                    >
                                      <NumberInputField
                                        id={ID.buildingCost}
                                        placeholder="원/평"
                                        onFocus={onAnyInputFocus}
                                        isDisabled={!canEditCostInputs}
                                        {...fieldProps}
                                      />
                                    </NumberInput>
                                  </FormControl>
                                </GridItem>

                                {/* 토목 */}
                                <GridItem>
                                  <FormControl>
                                    <FormLabel>토목 (원/평)</FormLabel>
                                    <NumberInput
                                      value={s2.토목 ?? ""}
                                      min={0}
                                      onChange={(_, v) => setS2((p) => ({ ...p, 토목: v }))}
                                      format={formatCost}
                                      parse={parseCost}
                                      isDisabled={!canEditCostInputs}
                                    >
                                      <NumberInputField
                                        placeholder="원/평"
                                        onFocus={onAnyInputFocus}
                                        isDisabled={!canEditCostInputs}
                                        {...fieldProps}
                                      />
                                    </NumberInput>
                                  </FormControl>
                                </GridItem>

                                {/* 전기 */}
                                <GridItem>
                                  <FormControl>
                                    <FormLabel>전기 (원/평)</FormLabel>
                                    <NumberInput
                                      value={s2.전기 ?? ""}
                                      min={0}
                                      onChange={(_, v) => setS2((p) => ({ ...p, 전기: v }))}
                                      format={formatCost}
                                      parse={parseCost}
                                      isDisabled={!canEditCostInputs}
                                    >
                                      <NumberInputField
                                        placeholder="원/평"
                                        onFocus={onAnyInputFocus}
                                        isDisabled={!canEditCostInputs}
                                        {...fieldProps}
                                      />
                                    </NumberInput>
                                  </FormControl>
                                </GridItem>

                                {/* 설비 */}
                                <GridItem>
                                  <FormControl>
                                    <FormLabel>설비 (원/평)</FormLabel>
                                    <NumberInput
                                      value={s2.설비 ?? ""}
                                      min={0}
                                      onChange={(_, v) => setS2((p) => ({ ...p, 설비: v }))}
                                      format={formatCost}
                                      parse={parseCost}
                                      isDisabled={!canEditCostInputs}
                                    >
                                      <NumberInputField
                                        placeholder="원/평"
                                        onFocus={onAnyInputFocus}
                                        isDisabled={!canEditCostInputs}
                                        {...fieldProps}
                                      />
                                    </NumberInput>
                                  </FormControl>
                                </GridItem>

                                {/* 경상비 */}
                                <GridItem>
                                  <FormControl>
                                    <FormLabel>경상비 (원/평)</FormLabel>
                                    <NumberInput
                                      value={s2.경상비 ?? ""}
                                      min={0}
                                      onChange={(_, v) => setS2((p) => ({ ...p, 경상비: v }))}
                                      format={formatCost}
                                      parse={parseCost}
                                      isDisabled={!canEditCostInputs}
                                    >
                                      <NumberInputField
                                        placeholder="원/평"
                                        onFocus={onAnyInputFocus}
                                        isDisabled={!canEditCostInputs}
                                        {...fieldProps}
                                      />
                                    </NumberInput>
                                  </FormControl>
                                </GridItem>

                                {/* 기타 */}
                                <GridItem>
                                  <FormControl>
                                    <FormLabel>기타 (원/평)</FormLabel>
                                    <NumberInput
                                      value={s2.기타 ?? ""}
                                      min={0}
                                      onChange={(_, v) => setS2((p) => ({ ...p, 기타: v }))}
                                      format={formatCost}
                                      parse={parseCost}
                                      isDisabled={!canEditCostInputs}
                                    >
                                      <NumberInputField
                                        placeholder="원/평"
                                        onFocus={onAnyInputFocus}
                                        isDisabled={!canEditCostInputs}
                                        {...fieldProps}
                                      />
                                    </NumberInput>
                                  </FormControl>
                                </GridItem>

                                {/* 공통 */}
                                <GridItem>
                                  <FormControl>
                                    <FormLabel>공통 (원/평)</FormLabel>
                                    <NumberInput
                                      value={s2.공통 ?? ""}
                                      min={0}
                                      onChange={(_, v) => setS2((p) => ({ ...p, 공통: v }))}
                                      format={formatCost}
                                      parse={parseCost}
                                      isDisabled={!canEditCostInputs}
                                    >
                                      <NumberInputField
                                        placeholder="원/평"
                                        onFocus={onAnyInputFocus}
                                        isDisabled={!canEditCostInputs}
                                        {...fieldProps}
                                      />
                                    </NumberInput>
                                  </FormControl>
                                </GridItem>
                                {/* 평당 공사비 (자동 계산 / 읽기 전용) */}
                                <GridItem>
                                  <FormControl isReadOnly>
                                    <FormLabel>평당 공사비 (원/평)</FormLabel>
                                    <Input
                                      value={Math.round(componentCostSum) > 0 ? Math.round(componentCostSum).toLocaleString() : ""}
                                      placeholder="자동 계산"
                                      isReadOnly
                                      variant="filled"
                                      bg={inputBg}
                                      borderColor={border}
                                      _readOnly={{ bg: "gray.50" }}
                                    />
                                    <FormHelperText fontSize="xs" color="gray.600" mt={1}>
                                      📢 <b>평당 공사비</b>는 항목(건축/토목/전기/설비/경상/기타/공통)를 입력하면 합계가 자동 계산됩니다.
                                    </FormHelperText>
                                  </FormControl>
                                </GridItem>
                              </Grid>
                            </VStack>
                          </SectionCard>
                        ) : (
                          // <Alert status="info" borderRadius="md">
                          //   <AlertIcon />
                          //   <Text fontSize="sm">
                          //     실제 비용 입력은 관리자 또는 슈퍼관리자만 이용할 수 있습니다.
                          //   </Text>
                          // </Alert>
                          null
                        )}
                      </VStack>
                    </Collapse>
                  </GridItem>

                </Grid>

                {/* 네비게이션 */}
                <Flex
                  justify="space-between"
                  pt={4}
                  direction={{ base: "column", sm: "row" }}
                  gap={{ base: 3, sm: 0 }}
                >
                  <Button variant="outline" onClick={() => setStep(1)} leftIcon={<ChevronLeftIcon />}>
                    이전
                  </Button>
                  <Button
                    colorScheme="blue"
                    onClick={() => setStep(3)}
                    isDisabled={!step2OK}
                    rightIcon={<ChevronRightIcon />}
                  >
                    다음
                  </Button>
                </Flex>
              </VStack>
            </Box>
          )}


            {/* STEP 3 */}
            {step === 3 && (
              <Box bg={cardBg} borderRadius="2xl" p={{ base:4, md:8 }} shadow={cardShadow} border="1px solid" borderColor={border}>
                <VStack align="stretch" spacing={6}>
                  <HStack>
                    <Box w={8} h={8} bg={accent} borderRadius="full" display="flex" alignItems="center" justifyContent="center">
                      <CheckIcon color="white"/>
                    </Box>
                    <Heading size={{ base:"md", md:"lg" }} color={textColor}>입력 정보 확인</Heading>
                  </HStack>

                  {/* 기본 정보 */}
                  <Badge colorScheme="purple" w="fit-content">기본 정보</Badge>
                  <SimpleGrid columns={{ base:1, md:2, lg:3 }} spacing={{ base:3, md:4 }}>
                    <InfoItem label="유형" value={s1.유형}/>
                    <InfoItem label="공사명" value={isNameComposing ? nameDraft : (nameDraft || s1.공사명)}/>
                    <InfoItem label="지역" value={s1.지역}/>
                    {/* <InfoItem label="시도" value={s1.시도}/> */}
                    <InfoItem label="구" value={s1.구} />
                    <InfoItem label="접수일" value={s1.접수일}/>
                  </SimpleGrid>

                  {/* 공사 개요 */}
                  <Badge colorScheme="blue" w="fit-content">공사 개요</Badge>
                  <SimpleGrid columns={{ base:1, md:2, lg:3 }} spacing={{ base:3, md:4 }}>
                    <InfoItem label="연면적(평)" value={s2.연면적}/>
                    <InfoItem label="마감수준" value={s2.마감수준}/>
                    <InfoItem label="세대 창호" value={s2["세대 창호"]}/>
                    {orderedStructureFields.map(({ key, label }) => (
                      <InfoItem key={key} label={`구조-${label}`} value={s2[key]}/>
                    ))}
                    <InfoItem label="공급면적(㎡)" value={s2.공급면적_m2}/>
                    <InfoItem label="대지 조건" value={formatSlopeLabel(s2["경사 여부"])}/>
                    <InfoItem label="동수" value={s2["동수"]}/>
                    <InfoItem label="대지면적(㎡)" value={s2["대지면적(㎡)"]}/>
                    <InfoItem label="수변 여부" value={String(s2["해안가 여부"]) === "1" ? "예" : "아니오"}/>
                    <InfoItem label="평균평형" value={s2.평균평형}/>
                    <InfoItem label="공기" value={s2.공기}/>
                    <InfoItem label="지상층수" value={s2.지상층수}/>
                    <InfoItem label="지하층수" value={s2.지하층수}/>
                    <InfoItem label="난방" value={s2.난방}/>
                    <InfoItem label="세대수" value={s2.세대수}/>
                    <InfoItem label="단지외공사" value={s2.단지외공사}/>
                    <InfoItem label="암반비율" value={s2.암반비율}/>
                    <InfoItem label="지하공법" value={s2.공법} /> 
                  </SimpleGrid>

                  {/* 선택 비용 요약 */}
                  <Badge colorScheme="teal" w="fit-content">비용</Badge>
                  <SimpleGrid columns={{ base:1, md:2, lg:3 }} spacing={{ base:3, md:4 }}>
                    <InfoItem label="총 공사비 (백만)" value={s2.공사비}/>
                    <InfoItem label="건축비 (원/평)" value={s2.건축비}/>
                    <InfoItem label="토목 (원/평)" value={s2.토목}/>
                    <InfoItem label="전기 (원/평)" value={s2.전기}/>
                    <InfoItem label="설비 (원/평)" value={s2.설비}/>
                    <InfoItem label="경상비 (원/평)" value={s2.경상비}/>
                    <InfoItem label="기타 (원/평)" value={s2.기타}/>
                    <InfoItem label="공통 (원/평)" value={s2.공통}/>
                    <InfoItem
                      label="평당 공사비 (원/평)"
                      value={
                        (() => {
                          const sum = Math.round(componentCostSum);
                          return sum > 0 ? sum.toLocaleString() : "-";
                        })()
                      }
                      note="📢 평당 공사비 = 아래 건축/토목/전기/설비/경상/기타/공통 단가 합계와 일치합니다."
                    />
                  </SimpleGrid>

                  <Flex justify="space-between" pt={6} direction={{ base:"column", sm:"row" }} gap={{ base:3, sm:0 }}>
                    <Button variant="outline" onClick={()=>setStep(2)} leftIcon={<ChevronLeftIcon/>}>이전</Button>
                    <Button colorScheme="blue"  leftIcon={<Wand2 />} onClick={handlePredict} isLoading={isSubmitting} loadingText="예측 중...">
                      AI 비용 예측하기
                    </Button>
                  </Flex>
                </VStack>
              </Box>
            )}
          </VStack>
        </Container>
      </Box>

      {/* 이력 모달 */}
      <Modal isOpen={historyModal.isOpen} onClose={historyModal.onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>불러오기</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <HStack mb={3} spacing={2} align="center">
              <Input size="sm" placeholder="프로젝트명 검색" value={historyQ} onChange={(e)=>setHistoryQ(e.target.value)} bg="white" />
              <Button size="sm" onClick={async()=>{ setHistoryLoading(true); try{ const res = await listPredictions({ page:1, page_size: historyPageSize, q: historyQ||undefined}); setHistoryItems(res?.items||[]); setHistoryTotal(res?.total||0); setHistoryPage(1);} catch(e){ toast({ status:'error', title:'검색 실패', description:String(e?.message||e) }); } finally{ setHistoryLoading(false);} }}>검색</Button>
            </HStack>
            {historyLoading ? (
              <HStack justify="center" py={8}><Spinner/></HStack>
            ) : (
              <>
                {isMobile ? (
                  <VStack align="stretch" spacing={3}>
                    {historyItems.length === 0 && (
                      <Box p={4} borderWidth="1px" borderRadius="lg" textAlign="center" color="gray.500">기록이 없습니다.</Box>
                    )}
                    {historyItems.map((r) => (
                      <Box key={r.id} p={4} borderWidth="1px" borderRadius="lg" bg="whiteAlpha.800">
                        <HStack justify="space-between" mb={2}>
                          <HStack>
                            <Tag colorScheme="blue">#{r.run_id ?? r.id}</Tag>
                            <Text fontSize="sm" color="gray.500">{(r.created_at || "").replace("T"," ").slice(0,19)}</Text>
                          </HStack>
                          <Button size="xs" colorScheme="blue" onClick={()=>fillFromHistory(r)}>가져오기</Button>
                        </HStack>
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="600">{r.project_name}</Text>
                          <HStack flexWrap="wrap" spacing={2}>
                            <Badge>{r.type || "-"}</Badge>
                            <Badge variant="outline" colorScheme="teal">단지외공사: {r.out_of_complex ?? "-"}</Badge>
                          </HStack>
                        </VStack>
                      </Box>
                    ))}
                  </VStack>
                ) : (
                  <Box overflowX="auto">
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>예측ID</Th><Th>생성일</Th><Th>공사명</Th><Th>유형</Th><Th>단지외공사</Th><Th>동작</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {historyItems.map((r)=>(
                          <Tr key={r.id}>
                            <Td><Tag>#{r.run_id ?? r.id}</Tag></Td>
                            <Td>{(r.created_at || "").replace("T"," ").slice(0,19)}</Td>
                            <Td>{r.project_name}</Td>
                            <Td>{r.type}</Td>
                            <Td>{r.out_of_complex ?? "-"}</Td>
                            <Td><Button size="xs" colorScheme="blue" onClick={()=>fillFromHistory(r)}>가져오기</Button></Td>
                          </Tr>
                        ))}
                        {historyItems.length === 0 && (
                          <Tr><Td colSpan={6}><Text color="gray.500">기록이 없습니다.</Text></Td></Tr>
                        )}
                      </Tbody>
                    </Table>
                  </Box>
                )}
                <HStack justify="space-between" align="center" pt={3}>
                  <Button size="sm" variant="outline" onClick={async()=>{ const p=Math.max(1, historyPage-1); setHistoryLoading(true); try{ const res=await listPredictions({ page:p, page_size: historyPageSize, q: historyQ||undefined}); setHistoryItems(res?.items||[]); setHistoryTotal(res?.total||0); setHistoryPage(p);} finally{ setHistoryLoading(false);} }} isDisabled={historyPage<=1}>이전</Button>
                  <Text fontSize="xs" color="gray.600">{historyPage} / {Math.max(1, Math.ceil((historyTotal||0)/historyPageSize))}</Text>
                  <Button size="sm" variant="outline" onClick={async()=>{ const maxP=Math.max(1, Math.ceil((historyTotal||0)/historyPageSize)); const p=Math.min(maxP, historyPage+1); setHistoryLoading(true); try{ const res=await listPredictions({ page:p, page_size: historyPageSize, q: historyQ||undefined}); setHistoryItems(res?.items||[]); setHistoryTotal(res?.total||0); setHistoryPage(p);} finally{ setHistoryLoading(false);} }} isDisabled={historyPage >= Math.max(1, Math.ceil((historyTotal||0)/historyPageSize))}>다음</Button>
                </HStack>
              </>
            )}
          </ModalBody>
          <ModalFooter><Button onClick={historyModal.onClose}>닫기</Button></ModalFooter>
        </ModalContent>
      </Modal>
      {isSubmitting && (
        <Portal>
          <Box
            position="fixed"
            inset="0"
            bg="rgba(255, 255, 255, 0.35)"
            backdropFilter="blur(8px)"
            zIndex={2000}
            display="flex"
            alignItems="center"
            justifyContent="center"
            px={4}
          >
            <VStack
              spacing={3}
              bg="white"
              borderRadius="2xl"
              p={6}
              shadow="2xl"
              maxW="xs"
              textAlign="center"
              border="1px solid"
              borderColor="gray.200"
            >
              <Spinner size="xl" thickness="4px" speed="0.7s" color="blue.500" />
              <Heading size="md">AI 비용 예측을 준비 중입니다</Heading>
              <Text fontSize="sm" fontWeight="600">
                {progressPercent}% 진행 중
              </Text>
              <Progress value={progressPercent} size="xs" colorScheme="blue" w="100%" hasStripe />
              <Text fontSize="sm" color="gray.600">
                입력하신 정보를 AI가 분석하고 있어요. 결과가 준비되면 자동으로 다음 화면으로 이동합니다.
              </Text>
            </VStack>
          </Box>
        </Portal>
      )}
    </Box>
  )
}
